import type { FloorInfo } from '@/services/dto/kiosk-config';
import { useConfigStore } from '@/stores';
import { getFloorMapName } from '@/utils/floor-guide';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SwiperRef } from 'swiper/react';

const getMaxStartIndex = (totalFloors: number, pageSize: number) => {
    if (totalFloors <= pageSize) {
        return 0;
    }

    return Math.floor((totalFloors - 1) / pageSize) * pageSize;
};

const getPageStartIndex = (floorIndex: number, pageSize: number, maxStartIndex: number) => {
    const pageStart = Math.floor(floorIndex / pageSize) * pageSize;
    return Math.min(pageStart, maxStartIndex);
};

export const useVerticalFloorManagement = (floorsParam: FloorInfo[], amountOfFloorsToShow = 5, selectedFloorName?: string) => {
    const swiperRef = useRef<SwiperRef>(null);
    const [isDisabledPrev, setIsDisabledPrev] = useState(false);
    const [isDisabledNext, setIsDisabledNext] = useState(false);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);

    const floors = useMemo(() => {
        if (!Array.isArray(floorsParam) || !floorsParam?.length) return [];
        return floorsParam;
    }, [floorsParam]);

    const maxStartIndex = useMemo(() => getMaxStartIndex(floors.length, amountOfFloorsToShow), [amountOfFloorsToShow, floors.length]);

    const selectedFloorIndex = useMemo(() => {
        if (!selectedFloorName || !floors.length) return -1;
        const index = floors.findIndex((floor) => getFloorMapName(floor) === selectedFloorName);
        return index >= 0 ? index : -1;
    }, [selectedFloorName, floors]);

    const shouldScroll = useMemo(() => floors.length > amountOfFloorsToShow, [floors, amountOfFloorsToShow]);

    const syncNavigationState = useCallback(
        (activeIndex: number) => {
            if (!shouldScroll) {
                setIsDisabledPrev(true);
                setIsDisabledNext(true);
                return;
            }

            setIsDisabledPrev(activeIndex <= 0);
            setIsDisabledNext(activeIndex + amountOfFloorsToShow >= floors.length);
        },
        [amountOfFloorsToShow, floors.length, shouldScroll]
    );

    const handleScrollUp = useCallback(() => {
        const swiper = swiperRef.current?.swiper;
        if (!swiper) return;

        const prevIndex = Math.max(0, swiper.activeIndex - amountOfFloorsToShow);
        swiper.slideTo(prevIndex);
    }, [amountOfFloorsToShow]);

    const handleScrollDown = useCallback(() => {
        const swiper = swiperRef.current?.swiper;
        if (!swiper) return;

        const nextIndex = Math.min(swiper.activeIndex + amountOfFloorsToShow, maxStartIndex);
        swiper.slideTo(nextIndex);
    }, [amountOfFloorsToShow, maxStartIndex]);

    const scrollToSelectedFloor = useCallback(() => {
        const swiper = swiperRef.current?.swiper;
        if (!swiper) {
            return;
        }

        const targetIndex =
            selectedFloorIndex >= 0 ? getPageStartIndex(selectedFloorIndex, amountOfFloorsToShow, maxStartIndex) : 0;

        swiper.slideTo(targetIndex, 0);
        syncNavigationState(swiper.activeIndex);
    }, [amountOfFloorsToShow, maxStartIndex, selectedFloorIndex, syncNavigationState]);

    const handleSlideChange = useCallback(
        (activeIndex: number) => {
            syncNavigationState(activeIndex);
        },
        [syncNavigationState]
    );

    useEffect(() => {
        scrollToSelectedFloor();
    }, [scrollToSelectedFloor, screenMode]);

    return {
        isDisabledPrev,
        isDisabledNext,
        shouldScroll,
        swiperRef,
        handleScrollUp,
        handleScrollDown,
        handleSlideChange,
        scrollToSelectedFloor,
    };
};
